import {
  Button,
  Chip,
  Collapse,
  IconButton,
  Paper,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import dayjs from 'dayjs'
import PropTypes from 'prop-types'
import React from 'react'

import {
  fmtDate,
  fmtDateTime,
  fmtMoney,
  fmtTime,
  titleCase,
  todayLocal
} from '../format'

/**
 * Bookings as an agenda: grouped by day, one dense row each, details on
 * expand. Scales to a full week of sessions where free-floating cards do not.
 *
 * Row: time · category · child · apartment (flat) · status · actions.
 * Expanded: parent + phone, grade, price/payment, adult-present flag,
 * arrival/start stamps, notes.
 */
const useStyles = makeStyles((theme) => ({
  day: { marginBottom: theme.spacing(3) },
  dayHead: {
    display: 'flex',
    alignItems: 'baseline',
    gap: theme.spacing(1),
    margin: theme.spacing(0, 0, 1, 0.5)
  },
  dayLabel: { fontWeight: 700 },
  dayCount: { color: theme.palette.text.secondary },
  today: { color: theme.palette.primary.main },
  row: {
    display: 'grid',
    gridTemplateColumns:
      '110px minmax(180px, 1.4fr) minmax(160px, 1fr) minmax(120px, auto) auto 32px',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(1, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    cursor: 'pointer',
    '&:last-child': { borderBottom: 'none' },
    '&:hover': { background: theme.palette.action.hover },
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '90px 1fr auto',
      '& $where, & $actions': { display: 'none' }
    }
  },
  time: { fontWeight: 600, whiteSpace: 'nowrap' },
  main: { minWidth: 0 },
  title: {
    fontWeight: 600,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  sub: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  where: {
    color: theme.palette.text.secondary,
    fontSize: 13,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  chips: { display: 'flex', gap: theme.spacing(0.5), flexWrap: 'nowrap' },
  actions: {
    display: 'flex',
    gap: theme.spacing(0.5),
    justifyContent: 'flex-end',
    whiteSpace: 'nowrap'
  },
  expand: { transition: 'transform 0.2s' },
  expanded: { transform: 'rotate(180deg)' },
  // Expanded details: a soft card aligned with the row's text column, facts
  // in a fixed grid so labels line up row to row, and the session log as a
  // three-step timeline rather than one run-on sentence.
  detail: {
    margin: theme.spacing(0, 2, 1.5, 15.5),
    padding: theme.spacing(1.5, 2),
    background: theme.palette.background.default,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    [theme.breakpoints.down('sm')]: { marginLeft: theme.spacing(2) }
  },
  facts: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: theme.spacing(1.5, 3)
  },
  dl: { fontSize: 13, lineHeight: 1.45 },
  dt: {
    color: theme.palette.text.secondary,
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    marginBottom: 2
  },
  warn: { color: theme.palette.primary.main, fontWeight: 600 },
  log: {
    display: 'flex',
    gap: theme.spacing(3),
    marginTop: theme.spacing(1.5),
    paddingTop: theme.spacing(1.5),
    borderTop: `1px dashed ${theme.palette.divider}`,
    flexWrap: 'wrap'
  },
  step: { fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 },
  dot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    background: theme.palette.text.disabled,
    flex: 'none'
  },
  dotDone: { background: theme.palette.primary.main },
  stepMuted: { color: theme.palette.text.disabled },
  notes: {
    fontSize: 13,
    marginTop: theme.spacing(1.5),
    paddingTop: theme.spacing(1.5),
    borderTop: `1px dashed ${theme.palette.divider}`,
    whiteSpace: 'pre-wrap'
  },
  empty: {
    color: theme.palette.text.secondary,
    padding: theme.spacing(4),
    textAlign: 'center'
  }
}))

const dayLabel = (iso) => {
  const d = dayjs(iso)
  const today = dayjs().startOf('day')
  if (d.isSame(today, 'day')) return 'Today'
  if (d.isSame(today.add(1, 'day'), 'day')) return 'Tomorrow'
  if (d.isSame(today.subtract(1, 'day'), 'day')) return 'Yesterday'
  return d.format('dddd')
}

const statusColor = (s) =>
  s === 'booked' ? 'primary' : s === 'no_show' ? 'secondary' : 'default'

const Row = ({ b, renderActions }) => {
  const classes = useStyles()
  const [open, setOpen] = React.useState(false)
  const r = b.report || {}
  const loc = b.location || {}
  const where = [loc.flat, loc.apartment].filter(Boolean).join(', ')
  return (
    <>
      <div
        className={classes.row}
        onClick={() => setOpen((o) => !o)}
        role="button"
        tabIndex={0}
      >
        <div className={classes.time}>
          {fmtTime(b.startTime)}
          <div className={classes.sub}>{b.durationMinutes} min</div>
        </div>
        <div className={classes.main}>
          <div className={classes.title}>
            {b.categoryName} · {b.childInfo?.name}
          </div>
          <div className={classes.sub}>
            {b.parentInfo?.name} · {b.parentInfo?.phone}
            {b.teacherName ? ` · ${b.teacherName}` : ''}
          </div>
        </div>
        <div className={classes.where} title={where}>
          {where || '—'}
        </div>
        <div className={classes.chips}>
          <Chip
            size="small"
            label={b.kind === 'trial' ? 'Trial' : 'Regular'}
            variant="outlined"
          />
          <Chip
            size="small"
            color={statusColor(b.status)}
            label={
              b.status === 'booked' && b.date < todayLocal()
                ? 'Not closed'
                : titleCase(b.status)
            }
          />
          {r.attended === true && (
            <Chip size="small" variant="outlined" label="Attended" />
          )}
          {r.attended === false && (
            <Chip size="small" variant="outlined" label="Not Attended" />
          )}
        </div>
        <div className={classes.actions} onClick={(e) => e.stopPropagation()}>
          {renderActions(b)}
        </div>
        <IconButton
          size="small"
          aria-label={open ? 'Hide details' : 'Show details'}
        >
          <ExpandMoreIcon
            className={`${classes.expand} ${open ? classes.expanded : ''}`}
            fontSize="small"
          />
        </IconButton>
      </div>
      <Collapse in={open} unmountOnExit>
        <div className={classes.detail}>
          <div className={classes.facts}>
            <div className={classes.dl}>
              <div className={classes.dt}>Child</div>
              {b.childInfo?.name}
              {b.grade ? ` · Grade ${b.grade}` : ''}
              {b.childInfo?.dateOfBirth ? (
                <div className={classes.sub}>
                  Born {fmtDate(b.childInfo.dateOfBirth)}
                </div>
              ) : null}
            </div>
            <div className={classes.dl}>
              <div className={classes.dt}>Parent</div>
              {b.parentInfo?.name}
              <div className={classes.sub}>{b.parentInfo?.phone}</div>
            </div>
            <div className={classes.dl}>
              <div className={classes.dt}>Location</div>
              {where || 'Apartment to be confirmed'}
              {loc.area ? <div className={classes.sub}>{loc.area}</div> : null}
            </div>
            <div className={classes.dl}>
              <div className={classes.dt}>Fee</div>
              {fmtMoney(b.payment_amount)}
              <div className={classes.sub}>{titleCase(b.payment_status)}</div>
            </div>
            <div className={classes.dl}>
              <div className={classes.dt}>Adult at home</div>
              {b.adult_presence_acknowledged ? (
                'Confirmed by parent'
              ) : (
                <span className={classes.warn}>
                  Not confirmed — check on arrival
                </span>
              )}
            </div>
          </div>
          <div className={classes.log}>
            {[
              ['Started', r.started_at],
              ['Completed', r.completed_at]
            ].map(([label, at]) => (
              <div
                key={label}
                className={`${classes.step} ${at ? '' : classes.stepMuted}`}
              >
                <span
                  className={`${classes.dot} ${at ? classes.dotDone : ''}`}
                />
                {label}
                {at ? ` · ${fmtDateTime(at)}` : ' · not yet'}
              </div>
            ))}
          </div>
          {r.notes && (
            <div className={classes.notes}>
              <div className={classes.dt}>Notes</div>
              {r.notes}
            </div>
          )}
        </div>
      </Collapse>
    </>
  )
}

const BookingsAgenda = ({
  rows,
  renderActions,
  emptyText,
  descending = false
}) => {
  const classes = useStyles()
  if (!rows.length) return <div className={classes.empty}>{emptyText}</div>

  const byDay = rows.reduce((acc, b) => {
    ;(acc[b.date] = acc[b.date] || []).push(b)
    return acc
  }, {})
  const days = Object.keys(byDay).sort()
  if (descending) days.reverse()

  return (
    <div>
      {days.map((date) => {
        const list = byDay[date].sort((a, b) =>
          a.startTime.localeCompare(b.startTime)
        )
        const isToday = dayjs(date).isSame(dayjs(), 'day')
        return (
          <div key={date} className={classes.day}>
            <div className={classes.dayHead}>
              <Typography
                variant="subtitle1"
                className={`${classes.dayLabel} ${
                  isToday ? classes.today : ''
                }`}
              >
                {dayLabel(date)}
              </Typography>
              <Typography variant="body2" className={classes.dayCount}>
                {dayjs(date).format('D MMM')} · {list.length}{' '}
                {list.length === 1 ? 'session' : 'sessions'}
              </Typography>
            </div>
            <Paper variant="outlined">
              {list.map((b) => (
                <Row key={b.uuid} b={b} renderActions={renderActions} />
              ))}
            </Paper>
          </div>
        )
      })}
    </div>
  )
}

BookingsAgenda.propTypes = {
  rows: PropTypes.array.isRequired,
  renderActions: PropTypes.func.isRequired,
  emptyText: PropTypes.string,
  descending: PropTypes.bool
}

export default BookingsAgenda
